import { useState, useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate } from 'react-router-dom';
import { EntityPageLayout } from '@/components/common';
import { Button } from '@/components/ui/button';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
} from '@/components/ui/drawer';
import { useIsMobile } from '@/hooks/use-mobile';
import { PermissionGate } from '@/components/auth/PermissionGate';
import { useAuth } from '@/hooks/useAuth';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import type { Organization } from '../types';

// Feature components
import {
  CreateOrganizationForm,
  EditOrganizationForm,
  OrganizationTable,
} from '../components';
import {
  useOrganizations,
  useCreateOrganization,
} from '../hooks/useOrganizationManagement';
import { type CreateOrganizationInput } from '../schemas/organizationSchemas';

export function OrganizationsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false);
  const isMobile = useIsMobile();
  const { hasPermission } = useAuth();
  // React Query hooks
  const { data, isLoading, error } = useOrganizations();
  const createOrganizationMutation = useCreateOrganization();

  const organizations = data?.organizations || [];
  const totalCount = data?.total_count || 0;
  const activeCount = data?.active_count || 0;

  // Handle /new route
  useEffect(() => {
    if (location.pathname === '/organizations/new') {
      if (hasPermission('organization_management:create')) {
        setIsCreateDrawerOpen(true);
      } else {
        // Redirect to main page if no permission
        navigate('/organizations');
      }
    }
  }, [location.pathname, hasPermission, navigate]);

  // Handle drawer close - navigate back to main page
  const handleCreateDrawerChange = (open: boolean) => {
    setIsCreateDrawerOpen(open);
    if (!open && location.pathname === '/organizations/new') {
      navigate('/organizations');
    }
  };

  // Handle create organization
  const handleCreateOrganization = async (values: CreateOrganizationInput) => {
    try {
      await createOrganizationMutation.mutateAsync(values);
      handleCreateDrawerChange(false);
    } catch {
      // Error is handled by the mutation hook
    }
  };

  // Render edit form for the drawer
  const renderEditForm = useCallback((organization: Organization, onClose: () => void) => {
    return (
      <EditOrganizationForm organization={organization} onClose={onClose} />
    );
  }, []);

  return (
    <>
      <Helmet>
        <title>Organizations - Urban Finch Admin</title>
      </Helmet>
      <EntityPageLayout
        title="Organizations"
        description="Manage organizations and their venues"
      permission="system_management:read"
      testIdPrefix="organizations"
      isLoading={isLoading}
      error={
        error instanceof Error
          ? error
          : error
            ? new Error(String(error))
            : undefined
      }
      headerActions={
        <PermissionGate permission="organization_management:create">
          <Button
            onClick={() => handleCreateDrawerChange(true)}
            data-testid="create-organization-button"
          >
            Create Organization
          </Button>
        </PermissionGate>
      }
    >
      <div className="space-y-6">
        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Organizations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" data-testid="org-total-count">
                {totalCount}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Active Organizations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div
                className="text-2xl font-bold"
                data-testid="org-active-count"
              >
                {activeCount}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Inactive Organizations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div
                className="text-2xl font-bold"
                data-testid="org-inactive-count"
              >
                {totalCount - activeCount}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Organization Table */}
        <Card>
          <CardHeader>
            <CardTitle>All Organizations</CardTitle>
            <CardDescription>
              A list of all organizations in the system
            </CardDescription>
          </CardHeader>
          <CardContent>
            <OrganizationTable
              data={organizations}
              renderEditForm={
                hasPermission('organization_management:update')
                  ? renderEditForm
                  : undefined
              }
            />
          </CardContent>
        </Card>
      </div>

      {/* Create Organization Drawer */}
      <Drawer
        open={isCreateDrawerOpen}
        onOpenChange={handleCreateDrawerChange}
        direction={isMobile ? 'bottom' : 'right'}
      >
        <DrawerContent className={isMobile ? 'h-[90vh]' : 'w-[400px] h-full'}>
          <DrawerHeader>
            <DrawerTitle>Create New Organization</DrawerTitle>
            <DrawerDescription>
              Add a new organization to the system
            </DrawerDescription>
          </DrawerHeader>
          <div className="px-4 flex-1 overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-gray-400">
            <CreateOrganizationForm onSubmit={handleCreateOrganization} />
          </div>
          <DrawerFooter>
            <Button
              type="submit"
              form="create-organization-form"
              disabled={createOrganizationMutation.isPending}
              data-testid="org-form-submit"
            >
              {createOrganizationMutation.isPending
                ? 'Creating...'
                : 'Create Organization'}
            </Button>
            <Button
              variant="outline"
              onClick={() => handleCreateDrawerChange(false)}
              disabled={createOrganizationMutation.isPending}
              data-testid="org-form-cancel"
            >
              Cancel
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </EntityPageLayout>
    </>
  );
}
